import { z } from "zod";
import { apiClient } from "../client";
import { collectionJobSchema, previewSearchResponseSchema } from "./schemas";
import type {
  CollectionJob,
  CreateCollectionJobRequest,
  PreviewSearchRequest,
  PreviewSearchResponse,
} from "./types";

const SUCCESS_STATUS_CODES = [200, 201, 202] as const;

function getStatusDescription(status: number): string {
  switch (status) {
    case 201:
      return "Created";
    case 202:
      return "Accepted";
    default:
      return "OK";
  }
}

function createPlaceholderJob(status: number): CollectionJob {
  return collectionJobSchema.parse({
    id: 0,
    status,
    statusDescription: getStatusDescription(status),
    createdDate: new Date().toISOString(),
    totalProfiles: 0,
    processedProfiles: 0,
  });
}

async function parseCollectionJobResponse(
  response: Response
): Promise<CollectionJob> {
  const isSuccessStatus = SUCCESS_STATUS_CODES.includes(
    response.status as (typeof SUCCESS_STATUS_CODES)[number]
  );

  if (isSuccessStatus) {
    const contentType = response.headers.get("content-type");
    const contentLength = response.headers.get("content-length");

    const hasNoContent =
      !contentType?.includes("application/json") ||
      contentLength === "0" ||
      !contentLength;

    if (hasNoContent) {
      return createPlaceholderJob(response.status);
    }

    try {
      const body = (await response.json()) as CollectionJob;
      return collectionJobSchema.parse(body);
    } catch {
      return createPlaceholderJob(response.status);
    }
  }

  const body = (await response.json()) as CollectionJob;
  return collectionJobSchema.parse(body);
}

export async function getCollectionJobs(): Promise<CollectionJob[]> {
  const response = await apiClient
    .get("collection-jobs", {})
    .json<CollectionJob[]>();

  return z.array(collectionJobSchema).parse(response);
}

export async function getCollectionJob(
  id: string | number
): Promise<CollectionJob> {
  const response = await apiClient
    .get(`collection-jobs/${id}`, {})
    .json<CollectionJob>();

  return collectionJobSchema.parse(response);
}

export async function createCollectionJob(
  data: CreateCollectionJobRequest
): Promise<CollectionJob> {
  const response = await apiClient.post("collection-jobs", { json: data });
  return parseCollectionJobResponse(response);
}

export async function cancelCollectionJob(
  id: string | number
): Promise<void> {
  await apiClient.post(`collection-jobs/${id}/cancel`, {});
}

export async function createClassificationOnlyJob(
  data: CreateCollectionJobRequest
): Promise<CollectionJob> {
  const response = await apiClient.post(
    "collection-jobs/classification-only",
    { json: data }
  );
  return parseCollectionJobResponse(response);
}

export async function previewSearch(
  data: PreviewSearchRequest
): Promise<PreviewSearchResponse> {
  const response = await apiClient
    .post("collection-jobs/preview", { json: data })
    .json<PreviewSearchResponse>();
  return previewSearchResponseSchema.parse(response);
}
